import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectLine, ConnectionStatus } from 'botframework-directlinejs';
import {
  Box,
  Typography,
  Button,
  TextField,
  Loading,
  _palette
} from '@p81-common/ui';
import createClasses from './styles';

interface Message {
  id: string;
  text: string;
  from: 'user' | 'bot';
  timestamp: Date;
  hasFeedback?: boolean;
  feedbackGiven?: 'up' | 'down' | null;
}

interface ChatWidgetProps {
  token: string;
  conversationId: string;
  streamUrl: string;
  directLineUrl: string;
  userId: string;
  onTokenExchange: (resourceUri: string) => Promise<string>;
}

interface Attachment {
  contentType: string;
  content?: {
    tokenExchangeResource?: { uri: string; id: string };
    connectionName?: string;
    body?: Array<{ type: string; text?: string }>;
  };
}

interface CardAction {
  type: string;
  title: string;
  value: string;
}

interface Activity {
  id?: string;
  type: string;
  name?: string;
  text?: string;
  timestamp?: string;
  from: { id: string; role?: string };
  attachments?: Attachment[];
  suggestedActions?: { actions: CardAction[]; to?: string[] };
  channelData?: { feedbackLoop?: { type: string } };
}

// botframework-directlinejs Activity type is narrower than the actual Bot Framework schema.
// This helper provides a single cast point for activities that exceed the library's type surface.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asActivity = (activity: Record<string, unknown>) => activity as any;

const OAUTH_CONTENT_TYPE = 'application/vnd.microsoft.card.oauth';
const ADAPTIVE_CARD_TYPE = 'application/vnd.microsoft.card.adaptive';
const SSO_LOGIN_PROMPT =
  "Hello! To be able to help you, I'll need you to sign in.";

function isBotActivity(activity: Activity): boolean {
  return activity.from.role === 'bot';
}

function findOAuthAttachment(activity: Activity): Attachment | null {
  if (!activity.attachments?.length) return null;
  return (
    activity.attachments.find(
      a =>
        a.contentType === OAUTH_CONTENT_TYPE &&
        a.content?.tokenExchangeResource?.uri
    ) ?? null
  );
}

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

function renderMessageText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MARKDOWN_LINK_REGEX.lastIndex = 0;
  while ((match = MARKDOWN_LINK_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length ? parts : text;
}

const ThumbUpIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const ThumbDownIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

function extractAdaptiveCardText(activity: Activity): string | null {
  const card = activity.attachments?.find(
    a => a.contentType === ADAPTIVE_CARD_TYPE && a.content?.body
  );
  if (!card?.content?.body) return null;

  const texts = card.content.body
    .filter(block => block.type === 'TextBlock' && block.text)
    .map(block => block.text!);

  return texts.length ? texts.join('\n') : null;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({
  token,
  conversationId,
  streamUrl,
  directLineUrl,
  userId,
  onTokenExchange
}) => {
  const { t } = useTranslation('copilotChat');
  const classes = createClasses();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSlowResponse, setIsSlowResponse] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<CardAction[]>([]);
  const directLineRef = useRef<DirectLine | null>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const slowResponseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const onTokenExchangeRef = useRef(onTokenExchange);
  const processedIdsRef = useRef<Set<string>>(new Set());
  const inputValueRef = useRef('');

  onTokenExchangeRef.current = onTokenExchange;
  inputValueRef.current = inputValue;

  useEffect(() => {
    const directLine = new DirectLine({
      token,
      conversationId,
      streamUrl,
      domain: `${directLineUrl}/v3/directline`,
      webSocket: true
    });

    directLineRef.current = directLine;

    let startConversationSent = false;
    const statusSub = directLine.connectionStatus$.subscribe(status => {
      setIsConnected(status === ConnectionStatus.Online);
      if (status === ConnectionStatus.Online && !startConversationSent) {
        startConversationSent = true;
        directLine
          .postActivity(
            asActivity({
              type: 'event',
              name: 'startConversation',
              from: { id: userId, role: 'user' }
            })
          )
          .subscribe({
            error: (err: unknown) =>
              console.error('DirectLine postActivity failed:', err)
          });
      }
    });

    const activitySub = directLine.activity$.subscribe(activity => {
      const act = activity as Activity;

      // Deduplicate — DirectLine can replay activities
      if (act.id && processedIdsRef.current.has(act.id)) return;
      if (act.id) {
        processedIdsRef.current.add(act.id);
        if (processedIdsRef.current.size > 500) processedIdsRef.current.clear();
      }

      // DirectLine assigns its own user ID — learn it from the first echo
      if (!isBotActivity(act)) {
        return;
      }

      // Typing indicator from bot
      if (activity.type === 'typing') {
        setIsTyping(true);
        return;
      }

      // SSO token exchange — intercept OAuth cards
      const oauthAttachment = findOAuthAttachment(act);
      if (oauthAttachment) {
        setIsTyping(true);
        const { uri, id } = oauthAttachment.content!.tokenExchangeResource!;
        const { connectionName } = oauthAttachment.content!;
        onTokenExchangeRef
          .current(uri)
          .then(userToken => {
            directLine
              .postActivity(
                asActivity({
                  type: 'invoke',
                  name: 'signin/tokenExchange',
                  value: { id, connectionName, token: userToken },
                  from: { id: userId, role: 'user' }
                })
              )
              .subscribe({
                error: (err: unknown) =>
                  console.error('DirectLine postActivity failed:', err)
              });
          })
          .catch(err => {
            console.error('SSO token exchange failed:', err);
            setMessages(prev => [
              ...prev,
              {
                id: `error-${Date.now()}`,
                text: t('SSO_TOKEN_EXCHANGE_FAILED'),
                from: 'bot',
                timestamp: new Date()
              }
            ]);
            setIsTyping(false);
          });
        return;
      }

      // Filter out SSO login prompt
      if (act.text === SSO_LOGIN_PROMPT) return;

      // Bot message — use text or fall back to adaptive card content
      if (activity.type === 'message') {
        // Capture suggested actions before any early return
        if (act.suggestedActions?.actions?.length) {
          setSuggestedActions(act.suggestedActions.actions);
        }

        const text = act.text || extractAdaptiveCardText(act);

        // Always clear typing/slow-response when the bot sends a message,
        // even if it only contains suggested actions and no text.
        setIsTyping(false);
        setIsSlowResponse(false);
        if (slowResponseTimeoutRef.current)
          clearTimeout(slowResponseTimeoutRef.current);

        if (!text) return;

        const hasFeedback = !!act.channelData?.feedbackLoop;

        setMessages(prev => [
          ...prev,
          {
            id: act.id || String(Date.now()),
            text,
            from: 'bot',
            timestamp: new Date(act.timestamp || Date.now()),
            hasFeedback,
            feedbackGiven: null
          }
        ]);
      }
    });

    return () => {
      directLine.end();
      statusSub.unsubscribe();
      activitySub.unsubscribe();
      clearTimeout(slowResponseTimeoutRef.current!);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, conversationId, streamUrl, directLineUrl, userId]);

  useEffect(() => {
    const list = messageListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, isTyping]);

  const handleSendMessage = useCallback(() => {
    const text = inputValueRef.current.trim();
    if (!text || !directLineRef.current) return;

    setMessages(prev => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        text,
        from: 'user',
        timestamp: new Date()
      }
    ]);
    setIsTyping(true);
    setIsSlowResponse(false);
    setInputValue('');

    directLineRef.current
      .postActivity(
        asActivity({
          type: 'message',
          text,
          from: { id: userId, role: 'user' }
        })
      )
      .subscribe({
        error: (err: unknown) =>
          console.error('DirectLine postActivity failed:', err)
      });

    if (slowResponseTimeoutRef.current)
      clearTimeout(slowResponseTimeoutRef.current);
    slowResponseTimeoutRef.current = setTimeout(
      () => setIsSlowResponse(true),
      90000
    );
  }, [userId]);

  const handleSuggestedAction = useCallback(
    (action: CardAction) => {
      if (!directLineRef.current) return;

      setMessages(prev => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          text: action.title,
          from: 'user',
          timestamp: new Date()
        }
      ]);
      setSuggestedActions([]);
      setIsTyping(true);
      setIsSlowResponse(false);

      directLineRef.current
        .postActivity(
          asActivity({
            type: 'message',
            text: action.value,
            from: { id: userId, role: 'user' }
          })
        )
        .subscribe({
          error: (err: unknown) =>
            console.error('DirectLine postActivity failed:', err)
        });

      if (slowResponseTimeoutRef.current)
        clearTimeout(slowResponseTimeoutRef.current);
      slowResponseTimeoutRef.current = setTimeout(
        () => setIsSlowResponse(true),
        90000
      );
    },
    [userId]
  );

  const handleFeedback = useCallback(
    (messageId: string, feedback: 'up' | 'down') => {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, feedbackGiven: feedback } : msg
        )
      );

      directLineRef.current
        ?.postActivity(
          asActivity({
            type: 'messageReaction',
            reactionsAdded: [{ type: feedback === 'up' ? 'like' : 'dislike' }],
            replyToId: messageId,
            from: { id: userId, role: 'user' }
          })
        )
        .subscribe({
          error: (err: unknown) =>
            console.error('DirectLine postActivity failed:', err)
        });
    },
    [userId]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const inputDisabled = !isConnected;
  const sendDisabled = !inputValue.trim() || inputDisabled;

  // Loading state — show loader until the bot's welcome message arrives
  if (messages.length === 0 && suggestedActions.length === 0) {
    return (
      <Box className={classes.welcomeContainer}>
        <Loading size="normal" />
        <Typography variant="body2" sx={{ color: _palette.grey[500], mt: 2 }}>
          {t('CONNECTING')}
        </Typography>
      </Box>
    );
  }

  // Chat state
  return (
    <Box className={classes.chatContainer}>
      {/* Message list */}
      <Box ref={messageListRef} className={classes.messageList}>
        {messages.map(message => {
          const isUser = message.from === 'user';
          return (
            <Box
              key={message.id}
              className={`${classes.messageBubble} ${
                isUser ? classes.userBubble : classes.botBubble
              }`}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                {renderMessageText(message.text)}
              </Typography>
              {!isUser && message.hasFeedback && (
                <Box className={classes.feedbackRow}>
                  <button
                    className={`${classes.feedbackButton} ${
                      message.feedbackGiven === 'up'
                        ? classes.feedbackButtonActive
                        : ''
                    }`}
                    onClick={() => handleFeedback(message.id, 'up')}
                    disabled={!!message.feedbackGiven}
                    title={t('FEEDBACK_LIKE')}
                  >
                    {ThumbUpIcon}
                  </button>
                  <button
                    className={`${classes.feedbackButton} ${
                      message.feedbackGiven === 'down'
                        ? classes.feedbackButtonActive
                        : ''
                    }`}
                    onClick={() => handleFeedback(message.id, 'down')}
                    disabled={!!message.feedbackGiven}
                    title={t('FEEDBACK_DISLIKE')}
                  >
                    {ThumbDownIcon}
                  </button>
                </Box>
              )}
            </Box>
          );
        })}

        {isTyping && (
          <Box className={classes.typingContainer}>
            <Box className={classes.typingBubble}>
              <Loading size="small" />
              <Typography variant="caption" sx={{ color: _palette.grey[500] }}>
                {t('THINKING')}
              </Typography>
            </Box>
            {isSlowResponse && (
              <Typography
                variant="caption"
                sx={{ color: _palette.grey[400], px: 1 }}
              >
                {t('SLOW_RESPONSE')}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {suggestedActions.length > 0 && (
        <Box className={classes.suggestedActionsRow}>
          {suggestedActions.map(action => (
            <Button
              key={action.value}
              variant="outlined"
              className={classes.suggestedActionButton}
              onClick={() => handleSuggestedAction(action)}
            >
              {action.title}
            </Button>
          ))}
        </Box>
      )}

      {/* Input row */}
      <Box className={classes.inputRow}>
        <TextField
          placeholder={t('INPUT_PLACEHOLDER')}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          size="medium"
          sx={{ flex: 1 }}
          disabled={inputDisabled}
        />
        <Button
          color="primary"
          onClick={handleSendMessage}
          disabled={sendDisabled}
        >
          {t('SEND_BUTTON')}
        </Button>
      </Box>
    </Box>
  );
};

export default ChatWidget;
